import React, {useState} from "react";
import {
  CROP_ASPECT_RATIO,
  CROP_ASPECT_RATIO_TOLERANCE,
  WORKSITE_REFERENCES_SECTIONS,
  WORKSITE_TECHNICAL_SHEET_SECTIONS
} from "../utils/constants";
import {EllipsisVerticalIcon} from "@heroicons/react/24/outline";
import {DragDropContext, Droppable, Draggable} from "react-beautiful-dnd";

// Components
import PrivateImage from "./PrivateImage";
import ReferenceJsonEditorSection from "./ReferenceJsonEditorSection";

// UI
import Toggle from "../ui-components/Toggle";

// Modals
import WorksiteImageModal from "./Modals/WorksiteImageModal";
import Tooltip from "../ui-components/Tooltip";


const ReferenceContent = ({intl, push, worksite, reference, setReference, countryApiRequest, editable}) => {

  const [imagesEdits, setImagesEdits] = useState();
  const [contentsEdits, setContentsEdits] = useState();
  const [technicalSheetEdits, setTechnicalSheetEdits] = useState();
  const [selectedImage, setSelectedImage] = useState();
  const [nonCompliantImages, setNonCompliantImages] = useState(new Set());

  const currentImagesArray = imagesEdits ?? reference.images_urls ?? [];

  const handleImagesDrop = (droppedItem) => {
    // ignore drop outside droppable container
    if (!droppedItem.destination || !imagesEdits)
      return;
    // copy current images array
    const updatedList = [...imagesEdits];
    // remove dragged item
    const [reorderedItem] = updatedList.splice(droppedItem.source.index, 1);
    // add dropped item
    updatedList.splice(droppedItem.destination.index, 0, reorderedItem);
    // update State
    setImagesEdits(updatedList);
  };

  const initImagesEdits = () => {
    const selectedOnly = reference.images_urls
      ?.map((img) => ({...img, selected: true}))
    ?? [];
    const unselectedOnly = worksite.images_urls?.data
      ?.filter((wimg) => !reference.images_urls?.some((rimg) => rimg.image_url === wimg.image_url))
      ?.map((img) => ({...img, selected: false}))
    ?? [];
    setImagesEdits(selectedOnly.concat(unselectedOnly));
  }

  const updateReference = async (edits) => {
    try {
      const result = await countryApiRequest.put(
        `/worksites_references/${reference.id}`,
        {
          ...reference,
          ...edits,
        }
      );
      setReference(result);
    } catch (error) {
      push({type: 'error', title: intl.formatMessage({id: 'an_error_occurred'})})
      throw error;
    }
  }

  const updateReferenceContent = async (obj) => {
    // sort edited images based on their original position
    const imagesToAdd = imagesEdits
      ?.filter((img) => img.selected)
      ?.map((img) => {
        const {selected, ...objWithoutSelected} = img;
        return objWithoutSelected;
      });

    // make an API call to update the current reference (note: will throw an error if anything goes wrong, so you
    // don't have to worry about the next statements - they won't be executed)
    await updateReference({content: obj, images_urls: imagesToAdd})

    setContentsEdits(null)
    setImagesEdits(null)
    push({type: 'success', title: intl.formatMessage({id: 'contents_updated_successfully'})})
  }

  const updateTechnicalSheetContent = async (obj) => {
    // make an API call to update the current reference (note: will throw an error if anything goes wrong, so you
    // don't have to worry about the next statements - they won't be executed)
    await updateReference({technical_sheet: obj})

    setTechnicalSheetEdits(null)
    push({type: 'success', title: intl.formatMessage({id: 'technical_sheet_updated_successfully'})})
  }

  const getJsonDefaultValue = (type) => {
    switch (type) {
      case 'natural':
        return {value: 0, um: ''};
      case 'items':
      case 'nested_items':
        return [''];
      default:
        return '';
    }
  }
  const mergeJsonStateAndStructure = (obj, structure) => ({
    ...Object.entries(structure).reduce(
      (prev, [field, { type }]) => ({
        ...prev,
        [field]: getJsonDefaultValue(type)
      }),
      {}
    ),
    ...(
      // overwrite with non-null entries from original object
      Object.entries(obj ?? {})
        .reduce(
          (prev, [field, value]) => (value ? {...prev, [field]: value} : prev),
          {}
        )
    )
  })

  function getImageSize(url, callback) {
    const img = new Image();
    img.src = url;
    img.onload = function() { callback(this.width, this.height); }
  }

  return <>
    {/* ---------- CONTENTS + IMAGES ---------- */}
    <ReferenceJsonEditorSection
      intl={intl}
      push={push}
      title="contents"
      editable={editable}
      structure={WORKSITE_REFERENCES_SECTIONS}
      value={reference.content}
      editedValue={contentsEdits}
      setEditedValue={setContentsEdits}
      onEdit={() => {
        setContentsEdits(mergeJsonStateAndStructure(reference.content, WORKSITE_REFERENCES_SECTIONS))
        initImagesEdits();
      }}
      onSave={updateReferenceContent}
      onCancel={() => { setContentsEdits(null); setImagesEdits(null); }}
      valueNotAvailableMessage={intl.formatMessage({id: 'contents_not_available'})}
    >
      {
        (imagesEdits ? worksite?.images_urls?.data?.length > 0 : reference?.images_urls?.length > 0) && <>
          <h3 className="mb-3">
            {intl.formatMessage({id: 'images'})}
          </h3>
          {
            imagesEdits
              ? (
                <DragDropContext onDragEnd={handleImagesDrop}>
                  <Droppable droppableId="images-droppable" direction="horizontal">
                    {(provided) => (
                      <div
                        className="flex flex-wrap gap-4"
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                      >
                        {
                          (imagesEdits ?? []).map((img, idx) => (
                            <Draggable key={idx} draggableId={`image-${idx}`} index={idx}>
                              {(provided) => (
                                <div
                                  className="rounded-md shadow-md p-3 border border-gray-100 bg-white"
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                >
                                  <div onClick={() => { setSelectedImage(idx); }}>
                                    <PrivateImage
                                      image_url={img.image_url}
                                      tag="div"
                                      className="w-28 bg-white rounded-lg shadow bg-cover cursor-pointer hover:opacity-70"
                                      onRetrievedSignedURL={(sUrl) => {
                                        // mark image as non-compliant if its aspect ratio is different from the preferred
                                        getImageSize(sUrl, (w, h) => {
                                          if (Math.abs(w / h - CROP_ASPECT_RATIO) < CROP_ASPECT_RATIO_TOLERANCE)
                                            return;
                                          setNonCompliantImages((prev) =>new Set([...prev, img.image_url]));
                                        })
                                      }}
                                    />
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center mt-3">
                                      <div {...provided.dragHandleProps}>
                                        <EllipsisVerticalIcon className="h-5 text-gray-400" />
                                      </div>
                                      {
                                        nonCompliantImages.has(img.image_url) && (
                                          <div className="mt-1">
                                            <Tooltip
                                              text={intl.formatMessage({id: 'non_standard_image_size_extended'})}
                                              placement="bottom"
                                              iconType="danger"
                                            />
                                          </div>
                                        )
                                      }
                                    </div>
                                    <Toggle
                                      checked={img.selected}
                                      onChange={(checked) => setImagesEdits(
                                        imagesEdits.map((inner, j) => ({
                                          ...inner,
                                          selected: j === idx ? checked : inner.selected
                                        }))
                                      )}
                                      flexAlignCls="justify-center"
                                    />
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          ))
                        }
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              )
              : <div className="flex flex-wrap gap-4">
                {
                  (reference.images_urls ?? []).map((img, idx) => (
                    <div key={idx} onClick={() => { setSelectedImage(idx); }}>
                      <PrivateImage
                        image_url={img.image_url}
                        tag="div"
                        className="h-24 w-24 bg-white rounded-lg shadow bg-cover cursor-pointer hover:opacity-70"
                      />
                    </div>
                  ))
                }
              </div>
          }
        </>
      }
    </ReferenceJsonEditorSection>

    {/* ---------- TECHNICAL SHEET ---------- */}
    <ReferenceJsonEditorSection
      intl={intl}
      push={push}
      title="technical_sheet"
      editable={editable}
      structure={WORKSITE_TECHNICAL_SHEET_SECTIONS}
      value={reference.technical_sheet}
      editedValue={technicalSheetEdits}
      setEditedValue={setTechnicalSheetEdits}
      onEdit={() => {
        setTechnicalSheetEdits(mergeJsonStateAndStructure(reference.technical_sheet, WORKSITE_TECHNICAL_SHEET_SECTIONS))
      }}
      onSave={updateTechnicalSheetContent}
      onCancel={() => { setTechnicalSheetEdits(null); }}
      valueNotAvailableMessage={intl.formatMessage({id: 'technical_sheet_not_available'})}
    />

    {/* ---------- MODALS ---------- */}
    {
      selectedImage !== undefined && (
        <WorksiteImageModal
          onNextImage={() => {
            setSelectedImage(selectedImage + 1 % currentImagesArray.length);
          }}
          onPrevImage={() => {
            const prevIdx = selectedImage - 1;
            setSelectedImage(prevIdx >= 0 ? prevIdx : currentImagesArray.length - 1);
          }}
          image={currentImagesArray[selectedImage]}
          closeModal={() => setSelectedImage(undefined)}
        />
      )
    }
  </>
}

export default ReferenceContent;